/**
 * NOS-127: Podcast feed metadata (RSS/iTunes). Requires org admin/owner (organizations RLS).
 */
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { MediaSectionNav } from "@/components/dashboard/MediaSectionNav"
import { PermissionGate } from "@/components/PermissionGate"
import { Navigate } from "react-router-dom"
import { useOrgStore } from "@/stores/orgStore"
import { useMediaSettings, type PodcastSettingsForm } from "@/hooks/useMediaSettings"
import { getPodcastFeedUrl } from "@/lib/podcastFeedUrl"
import { supabase } from "@/lib/supabase"
import { toast } from "sonner"

const LANGS = [
  { value: "en-us", label: "English (US)" },
  { value: "en-gb", label: "English (UK)" },
  { value: "es", label: "Spanish" },
]

export function MediaSettingsPage() {
  const { currentOrg } = useOrgStore()
  const orgSlug = currentOrg?.slug ?? ""
  const { settings, loading, saving, save, refresh } = useMediaSettings()
  const [form, setForm] = useState<PodcastSettingsForm>(settings)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    setForm(settings)
  }, [settings])

  const feedUrl = orgSlug ? getPodcastFeedUrl(orgSlug) : ""

  const handleCover = async (file: File | null) => {
    if (!file || !currentOrg?.id) return
    if (!file.type.startsWith("image/")) {
      toast.error("Upload an image file")
      return
    }
    setUploading(true)
    const path = `${currentOrg.id}/media/podcast-cover/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`
    const { error } = await supabase.storage.from("media").upload(path, file, { upsert: true })
    setUploading(false)
    if (error) {
      toast.error(error.message)
      return
    }
    const { data } = supabase.storage.from("media").getPublicUrl(path)
    setForm((f) => ({ ...f, cover_art_url: data.publicUrl }))
    toast.success("Cover art URL set — use at least 1400×1400 px for Apple Podcasts.")
  }

  const submit = async () => {
    const ok = await save(form)
    if (ok) toast.success("Podcast settings saved")
    else toast.error("Could not save")
    refresh()
  }

  return (
    <PermissionGate permission="can_manage_settings" fallback={<Navigate to="/dashboard/media" replace />}>
      <div className="min-w-0 mx-auto max-w-xl space-y-6">
        <div>
          <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2">
            <Link to="/dashboard/media">← Videos</Link>
          </Button>
          <MediaSectionNav />
          <h1 className="text-2xl font-bold">Podcast / RSS</h1>
          <p className="text-muted-foreground text-sm">
            Cover art should be a square JPEG or PNG, ideally 3000×3000 px (minimum 1400×1400 for Apple Podcasts).
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 rounded-lg border p-6">
            {feedUrl && (
              <div className="rounded-md bg-muted/50 p-3 text-sm">
                <div className="font-medium">Feed URL</div>
                <a href={feedUrl} className="text-primary break-all underline" target="_blank" rel="noreferrer">
                  {feedUrl}
                </a>
              </div>
            )}
            <div>
              <Label>Podcast title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
              />
            </div>
            <div>
              <Label>Author / host name</Label>
              <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
            </div>
            <div>
              <Label>Cover art</Label>
              <Input type="file" accept="image/*" disabled={uploading} onChange={(e) => handleCover(e.target.files?.[0] ?? null)} />
              {form.cover_art_url && (
                <p className="text-xs text-muted-foreground mt-1 break-all">{form.cover_art_url}</p>
              )}
            </div>
            <div>
              <Label>Language</Label>
              <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGS.map((l) => (
                    <SelectItem key={l.value} value={l.value}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="ex" checked={form.explicit} onCheckedChange={(c) => setForm({ ...form, explicit: c })} />
              <Label htmlFor="ex">Explicit content</Label>
            </div>
            <Button onClick={submit} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save podcast settings"}
            </Button>
          </div>
        )}
      </div>
    </PermissionGate>
  )
}
